import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import styled from 'styled-components';

import * as Sentry from '@sentry/react';
import amplitude from 'amplitude-js';
import { Button } from 'apps/auth/components/common/Button/Button';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { MarkedList } from 'apps/auth/components/common/MarkedList/MarkedList';
import { MarkedListItem } from 'apps/auth/components/common/MarkedList/MarkedListItem';
import BasePage from 'apps/auth/components/utils/BasePage';
import { INCREASE_LIMITS_FAQ_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { INCREASE_GA_LIMIT_ONFIDO_FRAME, INCREASE_GT_LIMIT_ONFIDO_FRAME, KYC_DOCUMENTS_LIST_CANADA } from 'apps/auth/constants/UrlConstants';
import { CANADA } from 'apps/auth/constants/WellKnownCountries';
import { addOnfido } from 'apps/auth/helpers/DOM';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import { OnfidoService } from 'apps/auth/services/OnfidoService';

export const OnfidoContainer = styled.div`
    position: relative;
    flex: 1;
`;

const OnfidoFramePage = () => {
    const [searchParams] = useSearchParams();
    const addParamToUrl = searchParams.toString() ? '?' + searchParams : '';

    const { userType } = useParams();
    const onfidoOutRef = useRef(null);

    const { countryId, ballId } = useAuth();
    const { suggestionTitle, suggestionSubtitle, suggestionReady } = usePageMessages();
    const { onfidoAccessToken, docs, docInfo, successURL, failURL, showDocTypeDescription, suggestionItems } = useExtra();

    const [showSuggestion, toggleSuggestion] = useState(showDocTypeDescription);
    const { theme } = useThemeDetector();

    const onComplete = () => {
        window.location.replace(successURL);
    };

    const onError = (data) => {
        Sentry.captureMessage('Onfido Frame error', {
            extra: {
                ball_id: ballId || '',
                countryId: countryId,
                data: data,
            },
            level: 'error',
        });

        if (data.message !== undefined) {
            window.location.replace(failURL + '?customErrorInfo=' + data.message);
        } else {
            window.location.replace(failURL);
        }
    };

    useEffect(() => {
        if (!showSuggestion && typeof window !== 'undefined') {
            if (typeof window.Onfido === 'undefined') {
                addOnfido(() => {
                    initOnfido();
                });
            } else {
                initOnfido();
            }
        }
        return () => {
            onfidoOutRef.current?.tearDown();
        };
    }, [showSuggestion]);

    const initOnfido = () => {
        let options;
        if (countryId === CANADA) {
            options = OnfidoService.getCanadianOptions(docInfo, docs?.requiredSelfie);
        } else {
            options = OnfidoService.getGT_Options(docInfo, docs?.requiredSelfie);
        }
        onfidoOutRef.current = OnfidoService.init(window.Onfido, onfidoAccessToken, options, onComplete, onError, theme);
    };

    const getBackUrl = () => {
        let backUrl = INCREASE_GT_LIMIT_ONFIDO_FRAME + addParamToUrl;
        if (countryId === CANADA) {
            backUrl = KYC_DOCUMENTS_LIST_CANADA + addParamToUrl;
        } else if (userType === 'ga') {
            backUrl = INCREASE_GA_LIMIT_ONFIDO_FRAME + addParamToUrl;
        }
        return backUrl;
    };

    if (showSuggestion) {
        return (
            <CommonPage backUrl={getBackUrl()} showChat={false}>
                <HeaderPanel title={suggestionTitle} subtitle={suggestionSubtitle} />
                <MarkedList>
                    {suggestionItems.map((item, index) => (
                        <MarkedListItem text={item} key={index} />
                    ))}
                </MarkedList>
                <FixedBottomPanel isRightAligned>
                    <Button
                        type="submit"
                        raised={true}
                        caption={suggestionReady}
                        onClick={() => {
                            amplitude.getInstance().logEvent(INCREASE_LIMITS_FAQ_SHOWN);
                            toggleSuggestion(false);
                        }}
                    />
                </FixedBottomPanel>
            </CommonPage>
        );
    } else {
        return (
            <CommonPage backUrl={getBackUrl()} showChat={false}>
                <OnfidoContainer id="onfido-mount" />
            </CommonPage>
        );
    }
};

export default BasePage(OnfidoFramePage);
